import axios, {
    AxiosHeaders,
    type AxiosAdapter,
    type AxiosRequestConfig,
    type AxiosResponseHeaders,
    type RawAxiosResponseHeaders,
} from 'axios';
import type { EnterApiConfig, MaybePromise } from '../config';
import { EnterHttpError } from './errors';
import { resolveRequestHeaders } from './headers';
import { type HttpExecutor, type RetryAfterWait, withRetryAfter } from './retry-after';
import type { EnterHttpClient, HttpRequestOptions } from './types';

/** 公共请求默认超时；长任务可以在单次请求中显式覆盖。 */
const DEFAULT_TIMEOUT_MS = 300_000;

interface BaseHttpDependencies {
    waitForRetry?: RetryAfterWait;
}

function normalizeBaseUrl(apiBaseUrl: string): string {
    const trimmed = apiBaseUrl.trim().replace(/\/+$/, '');
    if (!trimmed) {
        throw new Error('apiBaseUrl is required');
    }
    return trimmed;
}

/** 判断 body 是否已经是 Axios/浏览器可以直接发送的原生类型。 */
function isNativeRequestBody(
    body: unknown
): body is FormData | Blob | ArrayBuffer | ArrayBufferView | URLSearchParams | string {
    const isBlobLike =
        typeof body === 'object' &&
        body !== null &&
        (Object.prototype.toString.call(body) === '[object Blob]' ||
            (typeof Blob !== 'undefined' && body instanceof Blob));

    return (
        typeof body === 'string' ||
        body instanceof URLSearchParams ||
        isBlobLike ||
        body instanceof ArrayBuffer ||
        ArrayBuffer.isView(body) ||
        isFormDataBody(body)
    );
}

function isFormDataBody(body: unknown): body is FormData {
    return typeof FormData !== 'undefined' && body instanceof FormData;
}

function toHeaders(
    input: AxiosResponseHeaders | RawAxiosResponseHeaders | undefined
): Headers | undefined {
    if (!input) {
        return undefined;
    }

    const headers = new Headers();
    for (const [key, value] of Object.entries(input)) {
        if (value !== undefined) {
            headers.set(key, Array.isArray(value) ? value.join(', ') : String(value));
        }
    }
    return headers;
}

function toResponseType(
    responseType: HttpRequestOptions['responseType']
): AxiosRequestConfig['responseType'] {
    return responseType === 'arraybuffer' || responseType === 'blob' || responseType === 'text' || responseType === 'stream'
        ? responseType
        : 'json';
}

/** 将 Axios 错误转换成包内稳定的 EnterHttpError，屏蔽 Axios 细节。 */
export function toEnterHttpError(error: unknown): EnterHttpError {
    if (error instanceof EnterHttpError) {
        return error;
    }

    if (axios.isAxiosError(error)) {
        const responseBody = error.response?.data;
        return new EnterHttpError({
            status: error.response?.status,
            message:
                typeof responseBody === 'object' && responseBody && 'message' in responseBody
                    ? String((responseBody as { message?: unknown }).message)
                    : error.message || 'Network request failed',
            detail:
                typeof responseBody === 'object' && responseBody && 'detail' in responseBody
                    ? String((responseBody as { detail?: unknown }).detail)
                    : undefined,
            responseBody,
            headers: toHeaders(error.response?.headers),
            cause: error,
        });
    }

    return new EnterHttpError({
        message: error instanceof Error ? error.message : 'Network request failed',
        cause: error,
    });
}

/** 二进制下载的失败信封仍是 JSON；保留业务码，避免把升级提示误判成普通无权限。 */
async function normalizeResponseError(error: unknown): Promise<EnterHttpError> {
    const normalized = toEnterHttpError(error);
    const body = normalized.responseBody;
    const contentType = normalized.headers?.get('content-type') ?? '';
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
    const isBuffer = body instanceof ArrayBuffer || ArrayBuffer.isView(body);
    if (
        (!isBlob && !isBuffer) ||
        !/(?:\/json|\+json)(?:;|$)/i.test(contentType || (isBlob ? body.type : ''))
    ) {
        return normalized;
    }

    try {
        const text = isBlob
            ? await body.text()
            : new TextDecoder().decode(body as ArrayBuffer | ArrayBufferView);
        const parsed: unknown = JSON.parse(text);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return normalized;
        }
        return new EnterHttpError({
            status: normalized.status,
            message: 'message' in parsed ? String(parsed.message) : normalized.message,
            detail: 'detail' in parsed ? String(parsed.detail) : normalized.detail,
            responseBody: parsed,
            headers: normalized.headers,
            cause: normalized.cause,
        });
    } catch {
        // 非 JSON 或不可读的二进制失败保留原始错误，不覆盖 HTTP 状态。
        return normalized;
    }
}

async function notifySafely(callback: (() => Promise<void>) | undefined): Promise<void> {
    try {
        await callback?.();
    } catch {
        // 宿主观察回调失败时不能覆盖原始传输结果。
    }
}

function withFinalHttpErrorObserver(
    execute: HttpExecutor,
    onFinalHttpError?: (error: EnterHttpError) => MaybePromise<void>
): HttpExecutor {
    return async <T>() => {
        try {
            return await execute<T>();
        } catch (error) {
            if (error instanceof EnterHttpError) {
                await notifySafely(() => Promise.resolve(onFinalHttpError?.(error)));
            }
            throw error;
        }
    };
}

/**
 * 创建唯一的 Axios 传输层。
 * 所有领域模块都通过它获得统一的 headers、body、超时、错误和重试行为。
 */
export function createBaseHttp(
    config: EnterApiConfig,
    axiosAdapter?: AxiosAdapter,
    dependencies: BaseHttpDependencies = {}
): EnterHttpClient {
    const instance = axios.create({
        adapter: axiosAdapter,
        baseURL: normalizeBaseUrl(config.apiBaseUrl),
        timeout: config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });

    async function request<T = unknown>(
        path: string,
        options: HttpRequestOptions = {}
    ): Promise<T> {
        const method = options.method ?? 'GET';
        // 普通对象统一序列化为 JSON；Blob、FormData 等原生 body 保留给 Axios 处理。
        const hasJsonBody = options.body !== undefined && !isNativeRequestBody(options.body);
        const headers = await resolveRequestHeaders(
            hasJsonBody ? { 'Content-Type': 'application/json' } : undefined,
            config.getExtraHeaders,
            options.headers
        );
        const axiosHeaders = new AxiosHeaders(headers);
        if (isFormDataBody(options.body) || (!hasJsonBody && !axiosHeaders.has('Content-Type'))) {
            axiosHeaders.setContentType(false);
        }
        const axiosConfig: AxiosRequestConfig = {
            ...(options.responseType === 'stream' ? { adapter: axiosAdapter ?? 'fetch' } : {}),
            url: path,
            method,
            data: hasJsonBody ? JSON.stringify(options.body) : options.body,
            params: options.query,
            headers: axiosHeaders,
            responseType: toResponseType(options.responseType),
            withCredentials: options.withCredentials,
            signal: options.signal,
            timeout: options.timeoutMs ?? config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
            paramsSerializer: options.paramsSerializer
                ? {
                      serialize: (params) =>
                          options.paramsSerializer?.(
                              params as Record<string, string | number | boolean | null | undefined>
                          ) ?? '',
                  }
                : undefined,
            transformRequest: [(data) => data],
        };

        // 复用同一个 prepared config，保证重试时 headers、body、token 和 signal 不漂移。
        const executeOnce: HttpExecutor = async <T>() => {
            try {
                const response = await instance.request<T>(axiosConfig);
                options.validateResponse?.(response.data, toHeaders(response.headers));
                return response.data;
            } catch (error) {
                throw await normalizeResponseError(error);
            }
        };

        const retrying = withRetryAfter(
            executeOnce,
            {
                path,
                method,
                retry: options.retry,
                retryStatuses: options.retryStatuses,
                retryOnError: options.retryOnError,
                hasIdempotencyKey: Boolean(axiosHeaders.get('X-Idempotency-Key')),
                signal: options.signal,
            },
            options.errorHandling === 'caller' ? undefined : config.httpEvents,
            dependencies.waitForRetry
        );

        return withFinalHttpErrorObserver(
            retrying,
            options.errorHandling === 'caller' ? undefined : config.httpEvents?.onFinalHttpError
        )<T>();
    }

    return {
        request,
        get: (path, options) => request(path, { ...options, method: 'GET' }),
        post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
        put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
        patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
        delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
    };
}
